import { Node, NodeViewProps, mergeAttributes } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import {
  Fragment as ProseMirrorFragment,
  Node as ProseMirrorNode,
} from "@tiptap/pm/model";
import { useContext, useEffect, useState } from "react";
import { useDisclosure } from "../../../../ui/util/useDisclosure";
import { ArticleSectionStatus } from "../../../../__generated__/gql/graphql";
import RegenerateArticleSectionModal from "./RegenerateArticleSectionModal";
import Dropdown from "../../../../ui/Dropdown/Dropdown";
import { DotsVertical, Heading, Refresh } from "../../../../ui/Icon/Icon";
import IconButton from "../../../../ui/IconButton/IconButton";
import Badge from "../../../../ui/Badge/Badge";
import ArticleContext from "./ArticleContext";
import styles from "./Section.module.scss";
import { graphql } from "../../../../__generated__/gql";
import { useMutation } from "urql";
import classNames from "classnames";

const EnableSectionTitleMutation = graphql(/* GraphQL */ `
  mutation enableSectionTitle($id: String!) {
    updateArticleSection(input: { id: $id, titleDisabled: false }) {
      id
      titleDisabled
    }
  }
`);

const DisableSectionTitleMutation = graphql(/* GraphQL */ `
  mutation disableSectionTitle($id: String!) {
    updateArticleSection(input: { id: $id, titleDisabled: true }) {
      id
      titleDisabled
    }
  }
`);

const SectionNodeView = ({ node, editor, getPos }: NodeViewProps) => {
  const { sections } = useContext(ArticleContext);
  const [, enableSectionTitle] = useMutation(EnableSectionTitleMutation);
  const [, disableSectionTitle] = useMutation(DisableSectionTitleMutation);

  const section = sections.find((section) => section.id === node.attrs.id);
  const [regenerateArticleSectionModalOpened, regenerateArticleSectionModal] =
    useDisclosure();
  const [isRegenerating, setIsRegenerating] = useState(
    section?.status === ArticleSectionStatus.InProgress,
  );

  async function toggleTitleDisabled() {
    if (!section) {
      return;
    }

    if (section.titleDisabled) {
      await enableSectionTitle({ id: section.id });
    } else {
      await disableSectionTitle({ id: section.id });
    }
  }

  useEffect(() => {
    if (section?.status === ArticleSectionStatus.InProgress) {
      setIsRegenerating(true);
    }

    if (isRegenerating && section?.status === ArticleSectionStatus.Done) {
      editor
        .chain()
        .insertContentAt(
          {
            from: getPos() + 1,
            to: getPos() + node.nodeSize - 1,
          },
          `<sectionTitle>${section.title}</sectionTitle>${section.body ?? ""}`,
        )
        .setMeta("ignoreImages", true)
        .run();

      setIsRegenerating(false);
    }
  }, [editor, getPos, isRegenerating, node.nodeSize, section]);

  if (!section) {
    return null;
  }

  const { id, title, titleDisabled, additionalPrompt, articleId } = section;

  return (
    <NodeViewWrapper
      as="section"
      className={classNames(styles.section, {
        [styles.titleDisabled]: titleDisabled,
      })}
    >
      <div contentEditable="false" suppressContentEditableWarning>
        <Dropdown
          asChild
          items={[
            {
              items: [
                {
                  label: "Regenerate section",
                  IconComponent: Refresh,
                  onClick: regenerateArticleSectionModal.open,
                },
                {
                  label: `${
                    titleDisabled ? "Enable" : "Disable"
                  } section title`,
                  IconComponent: Heading,
                  onClick: toggleTitleDisabled,
                },
              ],
            },
          ]}
        >
          <IconButton
            IconComponent={DotsVertical}
            aria-label="Options"
            className={styles.optionsButton}
          />
        </Dropdown>
        <RegenerateArticleSectionModal
          id={id}
          additionalPrompt={additionalPrompt}
          articleId={articleId}
          isOpen={regenerateArticleSectionModalOpened}
          onClose={regenerateArticleSectionModal.close}
        />
      </div>
      {isRegenerating ? (
        <div contentEditable="false">
          <h2>{title}</h2>
          <Badge color="default" loading>
            Regenerating section
          </Badge>
        </div>
      ) : (
        <NodeViewContent />
      )}
    </NodeViewWrapper>
  );
};

const Section = Node.create<{ id: string; title: string; body: string }>({
  name: "section",

  group: "block",

  content: "sectionTitle block+",

  inline: false,

  selectable: false,

  defining: true,

  isolating: true,

  addProseMirrorPlugins() {
    return [
      new Plugin({
        filterTransaction: (transaction, state) => {
          // Avoid endless recursion when simulating the effects of the transaction
          if (transaction.getMeta("filteringRequiredNodeDeletion") === true) {
            return true;
          }

          transaction.setMeta("filteringRequiredNodeDeletion", true);

          // Simulate the transaction
          const newState = state.apply(transaction);

          function requiredNodes(node: ProseMirrorFragment) {
            const requiredNodes: ProseMirrorNode[] = [];

            node.descendants((child) => {
              if (child.type.name === "section") {
                requiredNodes.push(child);
                return false;
              }
            });

            return requiredNodes;
          }

          const isSectionDeleting =
            requiredNodes(state.doc.content).length !==
            requiredNodes(newState.doc.content).length;

          if (isSectionDeleting) {
            transaction.setMeta("sectionDeletionPrevented", true);
          }

          return !isSectionDeleting;
        },
      }),
    ];
  },

  addAttributes() {
    return {
      id: {
        parseHTML: (element) => element.getAttribute("data-id"),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "section",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["section", mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SectionNodeView);
  },
});

export default Section;
